import * as s from "../StatsStyled";
import "react-grid-layout/css/styles.css";
import { formatNumber } from "utils/functions";
import { IoIosTrendingUp, IoIosTrendingDown } from "react-icons/io";
import { Fragment, useCallback, useMemo } from "react";
import {
  createCardData,
  getUniqueLabelRowsData,
  CARD_INFOS,
  sortToRecorderType,
} from "utils/StatsUtil";
import { Layout } from "react-grid-layout";
import { useQuery } from "react-query";
import { useAppSelector } from "redux/hooks";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import {
  getDashboardSummaryAccount,
  getDashboardSummaryCamera,
  getDashboardSummaryRecorder,
  getDashboardSummaryUser,
} from "api/widgetAPI";
import { StatsSummaryCardData } from "api/interfaces/widgetInterface.interface";
import useStompDebouncedRefetch, {
  StompDateSignalKey,
} from "hook/useStompDebouncedRefetch";

export default function SummaryCard({
  cardLayouts,
  includeSuspended,
}: {
  cardLayouts: Layout[];
  includeSuspended: boolean;
}) {
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const summaryAccount = useQuery(
    ["summaryAccount", selectedAccount.accountId, includeSuspended],
    () =>
      getDashboardSummaryAccount({
        accountId: selectedAccount.accountId,
        includeSuspended: includeSuspended ?? false,
      }),
    {
      retry: 0,
      refetchOnWindowFocus: false,
    }
  );
  const summaryUser = useQuery(
    ["summaryUser", selectedAccount.accountId, includeSuspended],
    () =>
      getDashboardSummaryUser({
        accountId: selectedAccount.accountId,
        includeSuspended: includeSuspended ?? false,
      }),
    {
      retry: 0,
      refetchOnWindowFocus: false,
    }
  );
  const refetchAccount = useCallback(() => {
    summaryAccount.refetch();
    summaryUser.refetch();
  }, [summaryAccount, summaryUser]);
  useStompDebouncedRefetch({
    stompKey: ["organizationListUpdated"] as StompDateSignalKey[],
    refetch: refetchAccount,
  });
  const summaryRecorder = useQuery(
    ["summaryRecorder", selectedAccount.accountId, includeSuspended],
    () =>
      getDashboardSummaryRecorder({
        accountId: selectedAccount.accountId,
        includeSuspended: includeSuspended ?? false,
      }),
    {
      retry: 0,
      refetchOnWindowFocus: false,
    }
  );
  const summaryCamera = useQuery(
    ["summaryCamera", selectedAccount.accountId, includeSuspended],
    () =>
      getDashboardSummaryCamera({
        accountId: selectedAccount.accountId,
        includeSuspended: includeSuspended ?? false,
      }),
    {
      retry: 0,
      refetchOnWindowFocus: false,
    }
  );
  const refetchCamera = useCallback(() => {
    summaryRecorder.refetch();
    summaryCamera.refetch();
  }, [summaryCamera, summaryRecorder]);
  useStompDebouncedRefetch({
    stompKey: ["recorderListUpdated"] as StompDateSignalKey[],
    refetch: refetchCamera,
  });
  const summaryData: StatsSummaryCardData[] = useMemo(() => {
    const getResult = (key: string) => {
      switch (key) {
        case "recorderCount":
          return summaryRecorder.data?.result;
        case "cameraCount":
          return summaryCamera.data?.result;
        case "userCount":
          return summaryUser.data?.result;
        case "organizationCount":
          return summaryAccount.data?.result;
        default:
          return null;
      }
    };
    const getRows = (key: string): Array<{ label: string; value: number }> => {
      switch (key) {
        case "recorderCount":
          return getUniqueLabelRowsData(
            summaryRecorder.data?.result.details || [],
            "type",
            key
          );
        case "cameraCount":
          return sortToRecorderType(
            summaryCamera.data?.result.cameras || [],
            "recType"
          );
        case "userCount":
          return getUniqueLabelRowsData(
            summaryUser.data?.result.details || [],
            "type",
            key
          );
        case "organizationCount":
          return getUniqueLabelRowsData(
            summaryAccount.data?.result.details || [],
            "type",
            key
          );
        default:
          return [];
      }
    };
    return createCardData(
      "card",
      CARD_INFOS.filter((item) => item.graphType === "card"),
      cardLayouts
    )
      .filter((item) =>
        item.isRole.some((role) => selectedAccount.userPermission[role])
      )
      .map((item) => ({
        ...item,
        rows: getRows(item.key),
        transition: getResult(item.key)?.transition,
        total: getResult(item.key)?.count,
      }));
  }, [
    cardLayouts,
    selectedAccount.userPermission,
    summaryAccount.data?.result,
    summaryCamera.data?.result,
    summaryRecorder.data?.result,
    summaryUser.data?.result,
  ]);
  return (
    <s.Summary.Container>
      {summaryData.map(
        ({
          key,
          title,
          icon: Icon,
          rows,
          transition,
          total,
        }: StatsSummaryCardData) => {
          if (key === "cameraCount") {
            return (
              <s.Summary.Card key={key}>
                <s.Summary.CardHeader>
                  <s.Summary.Icon>{Icon && <Icon />}</s.Summary.Icon>
                  <s.Summary.CardTitle>{title}</s.Summary.CardTitle>
                  <s.Summary.Total status={transition}>
                    {formatNumber(total)}
                    {!!transition && (
                      <s.Summary.Transition transition={transition}>
                        {Number(transition) < 0 ? (
                          <IoIosTrendingDown size={16} />
                        ) : (
                          Number(transition) > 0 && (
                            <IoIosTrendingUp size={16} />
                          )
                        )}
                        {transition}
                      </s.Summary.Transition>
                    )}
                  </s.Summary.Total>
                </s.Summary.CardHeader>
                <s.Summary.CameraList>
                  {rows.map((detail, index) => {
                    const isA1 = (detail.recType ?? "")
                      .toLowerCase()
                      .includes("a1");
                    return (
                      <Fragment key={index}>
                        <label>{String(detail.recType || "")}</label>
                        <span>
                          {!isA1 && (
                            <>
                              <label>DWC:</label>{" "}
                              {formatNumber(Number(detail.dwc || 0))}
                            </>
                          )}
                        </span>
                        <span className={isA1 ? "text-right" : ""}>
                          {!isA1 && <label>ETC:</label>}
                          {formatNumber(Number(detail.etc || 0))}
                        </span>
                      </Fragment>
                    );
                  })}
                </s.Summary.CameraList>
              </s.Summary.Card>
            );
          }
          return (
            <s.Summary.Card key={key}>
              <s.Summary.CardHeader>
                <s.Summary.Icon>{Icon && <Icon />}</s.Summary.Icon>
                <s.Summary.CardTitle>{title}</s.Summary.CardTitle>
                <s.Summary.Total status={transition}>
                  {formatNumber(
                    rows.reduce((acc, curr) => acc + curr.value, 0)
                  )}
                  {!!transition && (
                    <s.Summary.Transition transition={transition}>
                      {Number(transition) < 0 ? (
                        <IoIosTrendingDown size={16} />
                      ) : (
                        Number(transition) > 0 && <IoIosTrendingUp size={16} />
                      )}
                      {transition}
                    </s.Summary.Transition>
                  )}
                </s.Summary.Total>
              </s.Summary.CardHeader>
              <s.Summary.List>
                {rows.map((detail, index) => (
                  <li key={index}>
                    <label>{String(detail.label || "")}</label>
                    <span>{formatNumber(Number(detail.value || 0))}</span>
                  </li>
                ))}
              </s.Summary.List>
            </s.Summary.Card>
          );
        }
      )}
    </s.Summary.Container>
  );
}
